import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { SearchHit, SearchResponse } from "@chat-yai/shared/admin-types";
import { searchApi } from "@/api/endpoints/search";
import { Icon } from "./Icon";

interface Props {
  open: boolean;
  onClose: () => void;
}

// Simple debounce hook so we only hit /admin/search after the user stops
// typing for 200ms (matches the WP-C plan).
function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

// Lightweight cmdk-style command palette. We chose to hand-roll instead of
// pulling the `cmdk` npm package: the surface needed (input + grouped list +
// arrow / enter / esc) is ~60 LOC, and avoiding the dependency keeps the
// admin bundle leaner and licence/audit footprint smaller.
export function CommandPalette({ open, onClose }: Props): JSX.Element | null {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const debounced = useDebounced(query, 200);

  const searchQuery = useQuery({
    queryKey: ["search", debounced],
    queryFn: ({ signal }) => searchApi.query(debounced, signal),
    enabled: open && debounced.length > 0,
    staleTime: 5_000,
  });

  // Reset state when re-opened so the previous search doesn't linger.
  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIdx(0);
      // Defer focus so the modal is mounted first.
      window.setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open]);

  // Flatten groups → list so arrow keys can traverse across groups.
  const flat: Array<SearchHit & { groupLabel: string }> = useMemo(() => {
    const data: SearchResponse | undefined = searchQuery.data;
    if (!data) return [];
    const out: Array<SearchHit & { groupLabel: string }> = [];
    for (const g of data.groups) {
      for (const item of g.items) {
        out.push({ ...item, groupLabel: g.label });
      }
    }
    return out;
  }, [searchQuery.data]);

  // Clamp active index whenever the list shrinks.
  useEffect(() => {
    if (activeIdx >= flat.length) setActiveIdx(0);
  }, [flat.length, activeIdx]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((idx) => (flat.length === 0 ? 0 : (idx + 1) % flat.length));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((idx) => (flat.length === 0 ? 0 : (idx - 1 + flat.length) % flat.length));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const hit = flat[activeIdx];
      if (hit) {
        navigate({ to: hit.href });
        onClose();
      }
    }
  };

  // Scroll active item into view (helps when the list is long).
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-cmdk-idx="${activeIdx}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [activeIdx]);

  if (!open) return null;
  return (
    <div
      className="cmdk-backdrop"
      role="dialog"
      aria-label="Command palette"
      onClick={onClose}
      onKeyDown={onKeyDown}
      data-testid="cmdk-backdrop"
    >
      <div
        className="cmdk-panel"
        onClick={(e) => e.stopPropagation()}
        data-testid="cmdk-panel"
      >
        <div className="cmdk-input-wrap">
          <Icon name="search" size={14} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIdx(0);
            }}
            placeholder="Search chatbots, sites, leads, conversations…"
            aria-label="Search"
            data-testid="cmdk-input"
            autoComplete="off"
          />
          <kbd className="cmdk-kbd">esc</kbd>
        </div>
        <div className="cmdk-list" ref={listRef} role="listbox">
          {!debounced ? (
            <div className="cmdk-empty">Start typing to search across this workspace.</div>
          ) : searchQuery.isLoading ? (
            <div className="cmdk-empty">Searching…</div>
          ) : flat.length === 0 ? (
            <div className="cmdk-empty" data-testid="cmdk-no-results">
              No results for &quot;{debounced}&quot;.
            </div>
          ) : (
            renderGrouped(flat, activeIdx, (idx) => {
              const hit = flat[idx];
              if (hit) {
                navigate({ to: hit.href });
                onClose();
              }
            })
          )}
        </div>
        <div className="cmdk-foot">
          <span>
            <kbd className="cmdk-kbd">↑</kbd>
            <kbd className="cmdk-kbd">↓</kbd> navigate
          </span>
          <span>
            <kbd className="cmdk-kbd">enter</kbd> open
          </span>
          <span>
            <kbd className="cmdk-kbd">esc</kbd> close
          </span>
        </div>
      </div>
    </div>
  );
}

function renderGrouped(
  flat: Array<SearchHit & { groupLabel: string }>,
  activeIdx: number,
  onSelect: (idx: number) => void,
): JSX.Element[] {
  const out: JSX.Element[] = [];
  let lastGroup = "";
  flat.forEach((hit, idx) => {
    if (hit.groupLabel !== lastGroup) {
      out.push(
        <div className="cmdk-group-label" key={`g-${hit.groupLabel}`}>
          {hit.groupLabel}
        </div>,
      );
      lastGroup = hit.groupLabel;
    }
    const active = idx === activeIdx;
    out.push(
      <button
        type="button"
        key={hit.kind + ":" + hit.id}
        data-cmdk-idx={idx}
        className={"cmdk-item " + (active ? "cmdk-active" : "")}
        onMouseEnter={() => onSelect.length /* keep linter quiet, no-op */}
        onClick={() => onSelect(idx)}
        role="option"
        aria-selected={active}
        data-testid={`cmdk-item-${hit.kind}-${hit.id}`}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 500 }}>{hit.title}</div>
          {hit.subtitle ? <div className="cmdk-item-sub">{hit.subtitle}</div> : null}
        </div>
        <span className="chip" style={{ textTransform: "capitalize" }}>
          {hit.kind}
        </span>
      </button>,
    );
  });
  return out;
}

// Hook that wires cmd+k / ctrl+k globally to toggle the palette. Components
// embed it once near the app shell so every page benefits.
export function useCommandPaletteHotkey(open: () => void): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        open();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
}
